import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Search, MoreVertical, Eye, CheckCircle, XCircle, Mail } from 'lucide-react';
import useApi from "../../api_service/useApi";
import LoadingStateComp from "@src/components/LoadingComponent/LoadingComponent";
import { ThirdPartyRequestData } from "./interface";
import { cn } from "@src/utils/app_functions";
import Pagination from "@src/components/Pagination/Pagination";
import { ThirdPartyRequestDetailView } from "./components/ViewResources";
import RequestJobProgressView from "./RequestJobProgressView";
import { toast } from "react-toastify";



type LayoutContextType = {
  setSchoolAdminPageName: React.Dispatch<React.SetStateAction<string>>;
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  setMenu: React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>;
  setIsAffiliate: React.Dispatch<React.SetStateAction<boolean>>;
};

export const debounce = <T extends (...args: any[]) => any>(func: T, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const StatusBadge = ({ status }: { status?: string }) => (
  <span className={cn(
    status === 'approved' && 'bg-green-100 text-green-800',
    status === 'pending' && 'bg-yellow-100 text-yellow-800',
    status === 'publishing' && 'bg-blue-100 text-blue-800',
    status === 'rejected' && 'bg-red-100 text-red-800',
    'px-3 py-1 rounded-full text-xs font-medium capitalize inline-block whitespace-nowrap'
  )}>
    {status || 'N/A'}
  </span>
);

const SSLIndicator = ({ resource }: { resource: ThirdPartyRequestData }) => {
  if (resource.status !== 'approved') return null;
  return resource.hasSSL ? (
    <span title="SSL Enabled" className="inline-flex shrink-0">
      <CheckCircle className="w-4 h-4 text-green-600" />
    </span>
  ) : (
    <span title="SSL Disabled" className="inline-flex shrink-0">
      <XCircle className="w-4 h-4 text-red-600" />
    </span>
  );
};

const DROPDOWN_MAX_HEIGHT = 240;

const ThirdPartyRequestsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [resources, setResources] = useState<ThirdPartyRequestData[]>([]);
  const [isViewPage, setIsViewPage] = useState(false);
  const [selectedResource, setSelectedResource] = useState<ThirdPartyRequestData | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number; openUp: boolean } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Job progress tracking state
  const [isJobProgressView, setIsJobProgressView] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(10);

  const path = window.location.pathname;
  const navigate = useNavigate();

  // Use our API hook
  const { loading, error, get } = useApi();
  const { loading: changeStatusLoading, post: changeStatusPost } = useApi();
  const { loading: resendMailLoading, post: resendMailPost } = useApi();
  

  // Function to fetch resources
  const fetchResources = useCallback(async (
    query = '',
    page: number = currentPage,
    showLoading: boolean = true
  ) => {
    // Construct query parameters
    let queryParams = `?page=${page}&limit=${pageSize}`;
    if (query) queryParams += `&search=${query}`;

    // Get authentication token from localStorage
    const token = localStorage.getItem('token');

    // Call API
    await get<{
      data: ThirdPartyRequestData[],
      pagination?: {
        totalPages: number,
        currentPage: number,
        totalItems: number
      }
    }, any>(
      `/get_systems_publish_request${queryParams}`,
      { Authorization: `Bearer ${token}` },
      {
        loading: showLoading,
        onSuccess: (response) => {
          if (response.data) {
            setResources(response.data);
          }
          if (response.pagination) {
            setTotalPages(response.pagination.totalPages);
            setCurrentPage(response.pagination.currentPage);
          }
        },
        onError: (err) => {
          console.error('Error fetching resources:', err);
        }
      }
    );
  }, [get]);

  const debouncedSearch = useRef(
    useCallback(
      debounce(
        (
          query: string,
        ) => {
          fetchResources(
            query,
            1, // Reset to first page on new search
            false,
          );
        },
        1000,
      ), // 1000ms delay
      [fetchResources],
    ),
  ).current;

  // Effect to trigger search on query change and page changes
  useEffect(() => {
    // When changing filters or search, reset to page 1
    if (currentPage === 1) {
      debouncedSearch(searchQuery);
    } else {
      // For page changes, use the current page
      fetchResources(searchQuery, currentPage);
    }
  }, [searchQuery, currentPage]); // Include currentPage in dependencies

  // Updated: Debounced handleSearchChange
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    // debouncedSearch(query); // Moved to useEffect for cleaner separation
  };

  const handleViewResource = (resourceId: string) => {
    const resource = resources.find((res) => res._id === resourceId);
    if (resource) {
      setSelectedResource(resource);
      setIsViewPage(true);
    }
  };

  const handleStatusChange = ( resourceId: string, statusValue: "approved" | "rejected") => {
    // Get authentication token from localStorage
    const token = localStorage.getItem("token");

    const formData = new FormData();
    formData.append("requestId", resourceId);
    formData.append("status", statusValue);

    changeStatusPost<{ jobId?: string }, any>(
      "/update_publish_request",
      formData,
      {
        Authorization: `Bearer ${token}`,
      },
      {
        method: "PATCH",
        onSuccess: (data) => {
          if (openDropdownId === resourceId) {
            setOpenDropdownId(null);
          }
          
          // Check if jobId exists in the response and navigate to job progress view
          if (data?.jobId) {
            setCurrentJobId(data.jobId);
            setIsJobProgressView(true);
          } else {
            // Fallback to refreshing resources if no jobId
            fetchResources();
          }
        },
        onError: (error: any) => {
          setOpenDropdownId(null);
          console.log(error);
        },
      }
    );
  };

  const handleViewProgress = ( jobId: string) => {
    navigate(`/home/third-party-requests/progress/${jobId}`);
  };

  const handleGenerateSSL = ( requestId: string) => {
    // Get authentication token from localStorage
    const token = localStorage.getItem("token");

    get<{ jobId?: string }, any>(
      `/request/generate/ssl/${requestId}`,
      {
        Authorization: `Bearer ${token}`,
      },
      {
        loading: true,
        onSuccess: (data) => {
          if (openDropdownId === requestId) {
            setOpenDropdownId(null);
          }
          
          // Check if jobId exists in the response and navigate to job progress view
          if (data?.jobId) {
            setCurrentJobId(data.jobId);
            setIsJobProgressView(true);
          } else {
            // Fallback to refreshing resources if no jobId
            fetchResources();
          }
        },
        onError: (error: any) => {
          setOpenDropdownId(null);
          console.log(error);
        },
      }
    );
  };

  const handleResendMail = (appId: string) => {
    if (!appId) {
      toast.error("App ID is missing");
      return;
    }

    const token = localStorage.getItem("token");

    const formData = new FormData();
    formData.append("appID", appId);

    resendMailPost<{ message?: string }, any>(
      "/third_party/resend_published_domain_mail",
      formData,
      {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      {
        onSuccess: (data) => {
          if (openDropdownId) {
            setOpenDropdownId(null);
          }
          setDropdownPosition(null);
          toast.success(data?.message || "Approval email resent successfully!");
          fetchResources();
        },
        onError: (error: any) => {
          if (openDropdownId) {
            setOpenDropdownId(null);
          }
          setDropdownPosition(null);
          toast.error(error?.errorMsg || "Failed to resend approval email");
          console.log(error);
        },
      }
    );
  };

  const toggleDropdown = (resourceId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (openDropdownId === resourceId) {
      setOpenDropdownId(null);
      setDropdownPosition(null);
      return;
    }

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const shouldOpenUp = rect.bottom + DROPDOWN_MAX_HEIGHT > window.innerHeight && rect.top > DROPDOWN_MAX_HEIGHT;

    setDropdownPosition({
      top: shouldOpenUp ? rect.top - 10 : rect.bottom + 8,
      right: window.innerWidth - rect.right,
      openUp: shouldOpenUp
    });
    setOpenDropdownId(resourceId);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchResources(searchQuery, page);
  };



  const {
    setSchoolAdminPageName,
    setShowInput,
  } = useOutletContext<LayoutContextType>();

  useEffect(() => {
    setSchoolAdminPageName("Thirdparty Requests");
    setShowInput(true);
  }, [path]);

  // Initial fetch of resources
  useEffect(() => {
    fetchResources('', 1);
  }, [fetchResources]);

  if (loading || changeStatusLoading || resendMailLoading) {
    return <LoadingStateComp Message="Thirdparty App Requests" />;
  }

  if (isJobProgressView && currentJobId) {
    return (
      <RequestJobProgressView
        jobId={currentJobId}
        onGoBack={() => {
          setIsJobProgressView(false);
          setCurrentJobId(null);
        }}
        onComplete={() => {
          setIsJobProgressView(false);
          setCurrentJobId(null);
          fetchResources();
        }}
      />
    );
  }

  if (isViewPage && selectedResource) {
    return (
      <ThirdPartyRequestDetailView
        resource={selectedResource}
        onGoBack={() => setIsViewPage(false)}
        onChangeStatus={() => {
          setIsViewPage(false)
          fetchResources();
        }}
      />
    )
  }

  return (
    <>
      <div className="w-full h-full pt-[32px]">
        <div className="px-2 lg:pr-[36px] lg:pl-[36px] space-y-2">
          <div className="flex flex-row items-center px-2 lg:pr-[28px] lg:pl-[28px] justify-between w-full min-h-[80px] py-2">
            <div className="flex flex-col ">
              <span className="text-xl font-medium leading-8 text-left">
                Gmind AI Third Party App Requests
              </span>
              <span className="text-sm font-normal text-[#707883] text-left">
                Explore articles, guides, and templates curated to boost your learning journey.
              </span>
            </div>
          </div>

          <div className="flex gap-4 border border-[#E8E8E8] rounded-[10px] p-2 lg:p-[16px] bg-[#F8F9FB]">
            <div className="flex w-full gap-4">
              <div className="relative flex-1 bg-[#FFFFFF] border border-[#E8E8E8] rounded-[10px] px-3 py-2 gap-[10px] flex items-center w-full">
                <Search className="absolute w-5 h-5 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
                <input
                  type="text"
                  placeholder="Search for class e.g Gmind Academy"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full mt-6 px-3 lg:px-[36px] pb-6">
          <div className="w-full bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {resources.length == 0 ? (
              <div className="flex flex-col items-center justify-center text-gray-500 w-full text-center px-6 py-12">
                <svg className="w-12 h-12 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium">No requests found</p>
                <p className="text-xs mt-1">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <>
                {/* Phones: one card per request */}
                <ul className="md:hidden divide-y divide-gray-200">
                  {resources.map((resource) => (
                    <li key={resource._id} className="p-4 flex gap-3">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900 break-words">{resource.systemApp?.appname}</p>
                          <StatusBadge status={resource.status} />
                        </div>
                        {resource.thirdPartyApp?.organizationName && (
                          <p className="text-sm text-gray-600 capitalize break-words">{resource.thirdPartyApp.organizationName}</p>
                        )}
                        {resource.thirdPartyApp?.email && (
                          <p className="text-sm text-gray-600 break-all">{resource.thirdPartyApp.email}</p>
                        )}
                        {resource.thirdPartyApp?.contactNumber && (
                          <p className="text-sm text-gray-600">{resource.thirdPartyApp.contactNumber}</p>
                        )}
                        {resource.systemApp?.domain && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                            <span className="break-all">{resource.systemApp.domain}</span>
                            <SSLIndicator resource={resource} />
                          </div>
                        )}
                      </div>
                      <button
                        className="self-start p-2 -mr-2 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={(e) => toggleDropdown(resource._id, e)}
                        aria-label="Actions"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-400" />
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Tablet and up: table; lower-priority columns drop out as the screen narrows */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left hidden xl:table-cell">
                          S/N
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">
                          Name
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left hidden lg:table-cell">
                          Organization
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">
                          Email
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left hidden xl:table-cell">
                          Contact
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">
                          Domain
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">
                          Status
                        </th>
                        <th className="px-4 xl:px-6 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right sticky right-0 bg-gray-50">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {resources.map((resource, index) => (
                        <tr key={resource._id} className="group hover:bg-gray-50 transition-colors">
                          <td className="px-4 xl:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden xl:table-cell">
                            {(currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td className="px-4 xl:px-6 py-4 text-sm min-w-[160px] max-w-[260px]">
                            <div className="font-medium text-gray-900 break-words">{resource.systemApp?.appname}</div>
                            {/* folded columns move under the name when they are hidden */}
                            <div className="lg:hidden text-gray-500 capitalize break-words">{resource.thirdPartyApp?.organizationName}</div>
                            {resource.thirdPartyApp?.contactNumber && (
                              <div className="xl:hidden text-gray-500">{resource.thirdPartyApp.contactNumber}</div>
                            )}
                          </td>
                          <td className="px-4 xl:px-6 py-4 text-sm text-gray-600 capitalize hidden lg:table-cell min-w-[160px] max-w-[240px] break-words">
                            {resource.thirdPartyApp?.organizationName}
                          </td>
                          <td className="px-4 xl:px-6 py-4 text-sm text-gray-600">
                            <span className="block truncate max-w-[200px] xl:max-w-[260px]" title={resource.thirdPartyApp?.email}>
                              {resource.thirdPartyApp?.email}
                            </span>
                          </td>
                          <td className="px-4 xl:px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden xl:table-cell">
                            {resource.thirdPartyApp?.contactNumber}
                          </td>
                          <td className="px-4 xl:px-6 py-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate max-w-[200px] xl:max-w-[260px]" title={resource.systemApp?.domain}>
                                {resource.systemApp?.domain}
                              </span>
                              <SSLIndicator resource={resource} />
                            </div>
                          </td>
                          <td className="px-4 xl:px-6 py-4 whitespace-nowrap">
                            <StatusBadge status={resource.status} />
                          </td>
                          {/* pinned so the menu stays reachable while the table scrolls sideways */}
                          <td className="px-4 xl:px-6 py-4 whitespace-nowrap text-right sticky right-0 bg-white group-hover:bg-gray-50 transition-colors">
                            <button
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              onClick={(e) => toggleDropdown(resource._id, e)}
                              aria-label="Actions"
                            >
                              <MoreVertical className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {resources.length > 0 && (
              <div className="px-4 xl:px-6 py-4 bg-gray-50 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {openDropdownId && dropdownPosition && (
        <div
          ref={dropdownRef}
          className={cn(
            "fixed w-56 max-w-[calc(100vw-1rem)] max-h-60 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-50",
            dropdownPosition.openUp ? "origin-bottom" : "origin-top"
          )}
          style={{
            top: dropdownPosition.openUp ? 'auto' : `${dropdownPosition.top}px`,
            bottom: dropdownPosition.openUp ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
            right: `${Math.max(dropdownPosition.right, 8)}px`,
          }}
        >
          <div className="py-1">
            <button
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
              onClick={() => {
                const resource = resources.find(r => r._id === openDropdownId);
                if (resource) {
                  handleViewResource(resource._id);
                  setOpenDropdownId(null);
                  setDropdownPosition(null);
                }
              }}
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>

             {resources.find(r => r._id === openDropdownId)?.status === 'publishing' && (
              <>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                  onClick={() => {
                    const value = resources.find(r => r._id === openDropdownId);
                    // console.log(value);
                    // console.log(value?.jobId);
                    handleViewProgress(value?.jobId ?? '');
                  }}
                >
                  <CheckCircle className="w-4 h-4" />
                  View Progress
                </button>
              </>
            )}


            {(resources.find(r => r._id === openDropdownId)?.status === 'approved' && !resources.find(r => r._id === openDropdownId)?.hasSSL) && (
              <>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                  onClick={() => {
                    const resource = resources.find(r => r._id === openDropdownId);
                    if (resource?._id) {
                      handleGenerateSSL(resource._id);
                    }
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Generate SSL
                </button>
              </>
            )}

            {resources.find(r => r._id === openDropdownId)?.status === 'approved' && (
              <>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                  onClick={() => {
                    const resource = resources.find(r => r._id === openDropdownId);
                    if (resource) {
                      const appId = resource.appID || resource.thirdPartyApp?.appID || '';
                      handleResendMail(appId);
                    }
                  }}
                >
                  <Mail className="w-4 h-4" />
                  Resend Approval Email
                </button>
              </>
            )}

            {resources.find(r => r._id === openDropdownId)?.status === 'pending' && (
              <>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                  onClick={() => {
                    handleStatusChange(openDropdownId, "approved");
                    setDropdownPosition(null);
                  }}
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </button>

                <button
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
                  onClick={() => {
                    handleStatusChange(openDropdownId, "rejected");
                    setDropdownPosition(null);
                  }}
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </>
  );
};

export default ThirdPartyRequestsPage;
